import { Button, FilterControls, pxToRem, Text } from '@lbcamden/lib-react';
import { getSortFn, parseDate } from '@lbcamden/shared-utils';
import groupBy from 'lodash/groupBy';
import { FunctionComponent, useMemo, useState } from 'react';

import { AddNewButton } from '~/client/components/common/Button/AddNewButton';
import { useFireSafetyNoteList, usePcfraDocumentsList } from '~/client/hooks/storeHooks';
import { FullPcfraPerson } from '~/common/types/people';

import {
  documentToHistoryItem,
  filterConfigs,
  getTimePeriodGroupName,
  noteToHistoryItem,
} from './helpers';
import { HistoryItem } from './HistoryItem';
import {
  GroupHeading,
  Timeline,
  ResidentHistoryRoot,
  TimelineToolbar,
  NoResults,
} from './ResidentHistory.styled';
import { ResidentHistoryFilterParams } from './types';

type Props = {
  person: FullPcfraPerson;
  onClickLogActivity(): void;
};

const itemsPerPage = 3;

export const ResidentHistory: FunctionComponent<Props> = ({
  person: { propertyId, personId },
  onClickLogActivity,
}) => {
  const notes = useFireSafetyNoteList(personId);
  const documents = usePcfraDocumentsList(personId);

  const [filterParams, setFilterParams] = useState<ResidentHistoryFilterParams>({});
  const [visibleItemCount, setVisibleItemCount] = useState(itemsPerPage);

  const { itemCount, canShowMore, groupedItems } = useMemo(() => {
    const noteItems = notes.map(noteToHistoryItem);
    const documentItems = documents.map(documentToHistoryItem);
    const allItems = [...noteItems, ...documentItems];
    const typeFilter = filterParams.type;

    const filteredItems = allItems.filter((item) => !typeFilter || typeFilter === item.type);

    const visibleItems = filteredItems
      .sort(getSortFn('desc', (item) => parseDate(item.timestamp)))
      .slice(0, visibleItemCount);

    return {
      itemCount: filteredItems.length,
      canShowMore: filteredItems.length > visibleItemCount,
      groupedItems: groupBy(visibleItems, getTimePeriodGroupName),
    };
  }, [notes, documents, filterParams, visibleItemCount]);

  const onClickShowMore = () => setVisibleItemCount((current) => current + itemsPerPage);

  return (
    <ResidentHistoryRoot>
      <Text css={{ fontSize: pxToRem(18) }} variant="h3">
        Resident history
      </Text>
      <Timeline>
        <TimelineToolbar>
          <FilterControls<ResidentHistoryFilterParams>
            filterConfigs={filterConfigs}
            onChange={setFilterParams}
          />
          <AddNewButton label="Log new activity" onClick={onClickLogActivity} />
        </TimelineToolbar>

        {Object.entries(groupedItems).map(([groupName, items]) => [
          <GroupHeading key={groupName}>
            <hr />
            <Text $color="textSecondary" $weight={600} variant="body2">
              {groupName}
            </Text>
            <hr />
          </GroupHeading>,
          ...items.map((item) => <HistoryItem key={item.id} propertyId={propertyId} item={item} />),
        ])}

        {!itemCount && <NoResults>No results found</NoResults>}

        {canShowMore && (
          <Button $color="secondary" onClick={onClickShowMore}>
            Show more
          </Button>
        )}
      </Timeline>
    </ResidentHistoryRoot>
  );
};
